import type {AxiosInstance} from 'axios'
import {attachAuthInterceptor} from './authInterceptor'
import {attachErrorInterceptor} from './errorInterceptor'
import {attachCacheInterceptor} from './cacheInterceptor'
import {attachThrottleInterceptor} from './throttleInterceptor'
import {attachMockInterceptor} from './mockInterceptor'

interface InterceptorOptions {
  enableAuth?: boolean
  enableError?: boolean
  enableMock?: boolean
}

/**
 * Default options for interceptors
 */
const defaultOptions: InterceptorOptions = {
  enableAuth: true,
  enableError: true,
  enableMock: true,
}

/**
 * Attaches all interceptors to an Axios instance based on provided options
 * @param axiosInstance - The Axios instance to attach interceptors to
 * @param options - Configuration options for interceptors
 * @returns The Axios instance with all specified interceptors attached
 */
export const attachInterceptors = (
  axiosInstance: AxiosInstance,
  options: InterceptorOptions = defaultOptions,
): AxiosInstance => {
  const mergedOptions = {...defaultOptions, ...options}
  let enhancedInstance = axiosInstance

  // Order: mock, throttle, cache, auth, error
  // Mock runs FIRST to redirect requests before any other processing
  // This ensures mock requests skip auth (no token needed for local mock server)
  if (mergedOptions.enableMock) {
    enhancedInstance = attachMockInterceptor(enhancedInstance)
  }
  enhancedInstance = attachThrottleInterceptor(enhancedInstance)
  enhancedInstance = attachCacheInterceptor(enhancedInstance)
  if (mergedOptions.enableAuth) {
    enhancedInstance = attachAuthInterceptor(enhancedInstance)
  }
  if (mergedOptions.enableError) {
    enhancedInstance = attachErrorInterceptor(enhancedInstance)
  }
  return enhancedInstance
}
